import * as React from 'react';
import logo from '@/assets/logo.svg';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import HomeIcon from '@/frames/components/leftPannel/components/menu/components/icons/HomeIcon';
import DashboardIcon from '@/frames/components/leftPannel/components/menu/components/icons/DashboardIcon';
import AnalyticsIcon from '@/frames/components/leftPannel/components/menu/components/icons/AnalysisIcon';
import NewsIcon from '@/frames/components/leftPannel/components/menu/components/icons/NewsIcon';
import SettingsIcon from '@/frames/components/leftPannel/components/menu/components/icons/SettingsIcon';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import LanguageSetting from '@/ui/languageSetting';
import lang from '@/assets/lang.svg';
import { LogOut, PanelLeft, PanelLeftClose } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const [t] = useTranslation('mainframe');
  const user = useSelector((state: any) => state.user.info);
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('state');
    window.location.href = '/signin';
  };
  const data = {
    navMain: [
      {
        title: t('Pages'),
        url: '/',
        items: [
          {
            title: t('Dashboard'),
            url: '/dashboard',
            isActive: true,
            icon: <DashboardIcon />,
          },
          {
            title: t('Home'),
            url: '/',
            isActive: true,
            icon: <HomeIcon />,
          },
          {
            title: t('Analytics'),
            url: '/analytics',
            // isActive: Object.keys(user).length > 0,
            isActive: true,
            icon: <AnalyticsIcon />,
          },
          {
            title: t('News'),
            url: '/news',
            isActive: true,
            icon: <NewsIcon />,
          },
          {
            title: t('Account Settings'),
            url: '/settings',
            isActive: Object.keys(user).length > 0,
            icon: <SettingsIcon />,
            isHidden: Object.keys(user).length === 0,
          },
        ],
      },
    ],
  };

  const { open, isMobile, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  return (
    <Sidebar collapsible="icon" className="group" {...props}>
      <SidebarHeader>
        <div
          className={cn(
            'flex justify-between items-center gap-2 overflow-hidden',
            open && 'py-4 px-2',
          )}
        >
          <div
            className={cn(
              'bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg',
              open ? '' : 'group-hover:hidden',
            )}
          >
            <img src={logo} alt="logo" width={open ? '32px' : '24px'} />
          </div>
          <Button
            data-sidebar="trigger"
            size="icon"
            variant="ghost"
            className=""
            onClick={() => {
              toggleSidebar();
            }}
          >
            <PanelLeft />
          </Button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {data.navMain.map((item) => (
          <SidebarGroup key={item.title}>
            <SidebarGroupLabel>{item.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {item.items.map((item) => (
                  <SidebarMenuItem key={item.title} className={item.isHidden ? 'hidden' : ''}>
                    <SidebarMenuButton
                      tooltip={item.title}
                      className="hover:bg-muted"
                      disabled={!item.isActive}
                      onClick={() => navigate(item.url)}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
        <SidebarGroup className={open ? '' : 'hidden'}>
          <SidebarGroupLabel>{t('Settings')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenuItem title={t('Language')} className="flex justify-between">
              <SidebarMenuButton className="flex items-center gap-2">
                <img src={lang} alt="lang" className="w-4" />
                <span>{t('Language')}</span>
              </SidebarMenuButton>
              <LanguageSetting />
            </SidebarMenuItem>
          </SidebarGroupContent>
        </SidebarGroup>
        {isMobile && (
          <SidebarGroup className={open ? '' : 'hidden'}>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem title={t('Signout')} className="flex justify-between">
                  <SidebarMenuButton
                    className="flex items-center gap-2 hover:bg-muted"
                    onClick={logout}
                  >
                    <LogOut size={16} strokeWidth={1.5} />
                    <div>{t('Signout')}</div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
